import { useMemo, useState } from "react";
import {
  countByDate,
  last30DayLabels,
  mondayFirstMonthCells,
  monthDayLabels,
  singaporeYmd,
  sumDayCounts,
  toDayLabel,
} from "../utils/portalDayCount";

function monthTitle(year, month, lang) {
  const date = new Date(Date.UTC(year, month - 1, 1));
  return new Intl.DateTimeFormat(lang === "cn" ? "zh-CN" : "en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

function PortalStatsCards({
  createdRows,
  approvedRows,
  rejectedCount,
  onRejectedClick,
  lang,
  t,
}) {
  const today = singaporeYmd();
  const [view, setView] = useState({ year: today.year, month: today.month });

  const last30 = useMemo(() => last30DayLabels(), []);
  const created30 = useMemo(() => sumDayCounts(createdRows, last30), [createdRows, last30]);
  const approved30 = useMemo(() => sumDayCounts(approvedRows, last30), [approvedRows, last30]);
  const createdMap = useMemo(() => countByDate(createdRows), [createdRows]);
  const monthCreated = useMemo(
    () => sumDayCounts(createdRows, monthDayLabels(view.year, view.month)),
    [createdRows, view.year, view.month]
  );
  const cells = useMemo(
    () => mondayFirstMonthCells(view.year, view.month),
    [view.year, view.month]
  );
  const weekdays = lang === "cn" ? t.weekdaysCn : t.weekdaysEn;
  const todayLabel = toDayLabel(today);

  function shiftMonth(delta) {
    setView((current) => {
      const next = new Date(Date.UTC(current.year, current.month - 1 + delta, 1));
      return { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1 };
    });
  }

  return (
    <div className="mb-6 grid grid-cols-1 items-stretch gap-4 md:grid-cols-3">
      <article className="rounded-2xl bg-white p-5 shadow-sm">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">{t.last30Days}</h3>
        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-slate-500">{t.created}</dt>
            <dd className="text-2xl font-semibold tabular-nums text-slate-900">{created30}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-slate-500">{t.approved}</dt>
            <dd className="text-2xl font-semibold tabular-nums text-slate-900">{approved30}</dd>
          </div>
        </dl>
      </article>

      <button
        type="button"
        className="flex flex-col justify-start rounded-2xl bg-white p-5 text-left shadow-sm"
        onClick={onRejectedClick}
      >
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">{t.rejected}</h3>
        <dl className="mt-4">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <dt className="text-slate-500">{t.rejected}</dt>
            <dd
              className={`text-2xl font-semibold tabular-nums ${
                rejectedCount > 0 ? "text-rose-700" : "text-slate-900"
              }`}
            >
              {rejectedCount}
            </dd>
          </div>
        </dl>
      </button>

      <article className="rounded-2xl bg-white p-4 shadow-sm md:p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="flex items-baseline gap-2 text-sm font-semibold uppercase tracking-wide text-slate-400">
            {t.calendar}
            <span className="text-lg font-semibold tabular-nums text-slate-900">{monthCreated}</span>
          </h3>
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="rounded-md px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-100"
              onClick={() => shiftMonth(-1)}
            >
              {t.calendarPrev}
            </button>
            <p className="min-w-[7.5rem] text-center text-xs font-semibold text-slate-800">
              {monthTitle(view.year, view.month, lang)}
            </p>
            <button
              type="button"
              className="rounded-md px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-100"
              onClick={() => shiftMonth(1)}
            >
              {t.calendarNext}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] font-medium uppercase tracking-wide text-slate-400">
          {weekdays.map((label) => (
            <div key={label} className="py-0.5">
              {label}
            </div>
          ))}
        </div>
        <div className="mt-0.5 grid grid-cols-7 gap-0.5">
          {cells.map((day, index) => {
            if (!day) {
              return <div key={`empty-${index}`} className="min-h-[2.4rem] rounded bg-slate-50" />;
            }
            const label = toDayLabel({ year: view.year, month: view.month, day });
            const count = createdMap[label] || 0;
            const isToday = label === todayLabel;
            return (
              <div
                key={label}
                className={`min-h-[2.4rem] rounded px-0.5 py-0.5 ${
                  isToday ? "bg-[#fbf6ea] ring-1 ring-[#c9a46a]" : "bg-slate-50"
                }`}
              >
                <div className="text-[10px] leading-none text-slate-500">{day}</div>
                <div className="mt-0.5 text-sm font-semibold leading-none tabular-nums text-slate-900">
                  {count}
                </div>
              </div>
            );
          })}
        </div>
      </article>
    </div>
  );
}

export default PortalStatsCards;
